import { Button } from '@shared/ui-react/Button';
import * as React from 'react';
import { observer } from 'mobx-react';
import { LongSectionStore } from '@pages/cross-section/long-section/LongSectionStore';
import { CrossSectionStore } from '@pages/cross-section/cross-section/CrossSectionStore';
import { observable, action, makeObservable, autorun } from 'mobx';
import { LStorage } from '@shared/Storage';


@observer
export class CrossSectionModelsListComponent extends React.Component<{
    store: CrossSectionStore | LongSectionStore;
    hasPipes: boolean;
    hasLayers: boolean;
}> {
    storageKey = 'CrossSection_showLayersInUI';
    @observable showLayersInUI: boolean = JSON.parse(LStorage.getItem(this.storageKey) ?? 'true');

    constructor(props: any) {
        super(props);
        makeObservable(this);

        autorun(() => {
            LStorage.setItem(this.storageKey, this.showLayersInUI);
        })
    }

    toggleShowLayers = () => {
        this.showLayersInUI = !this.showLayersInUI;
    }

    loadAllProjectModels = () => {
        window.DialogConfirm({
            msg: kuura.getMessage('general.warning.slow_action'),
        }, () => {
            this.props.store.loadSelection.includeModelIds = null;
            this.props.store.fetchDrawing();
        })
    }

    toggleCheckPipe = (id: number) => {
        if (this.props.store.hiddenPipeIds.includes(id)) {
            this.props.store.hiddenPipeIds = this.props.store.hiddenPipeIds.filter(x => x !== id);
        } else {
            this.props.store.hiddenPipeIds.push(id);
        }
        this.props.store.drawAll();
    }

    toggleCheckModel = (id: number | string, children?: { modelId: string }[]) => {
        if (this.props.store.hiddenModelIds.includes(id)) {
            this.props.store.hiddenModelIds = this.props.store.hiddenModelIds.filter(x => ![id, ...(children ?? []).map(c => c.modelId)].includes(x));
        } else {
            this.props.store.hiddenModelIds.push(id);
            children?.forEach(c => this.props.store.hiddenModelIds.push(c.modelId));
        }
        this.props.store.drawAll();
    }

    toggleCheckLogpoints = (id: number) => {
        if (this.props.store.hiddenLogpointIds.includes(id)) {
            this.props.store.hiddenLogpointIds = this.props.store.hiddenLogpointIds.filter(x => x !== id);
        } else {
            this.props.store.hiddenLogpointIds.push(id);
        }
        this.props.store.drawAll();
    }

    toggleCheckAllModels = () => {
        if (this.props.store.hiddenModelIds.length === 0) {
            this.props.store.hiddenModelIds = Array.from(new Set([
                ...this.props.store.latestServerData.models.map(x => x.modelId),
                ...this.props.store.latestServerData.models.flatMap(model => model.lines.map(l => l.id))
            ]));
        } else {
            this.props.store.hiddenModelIds = [];
        }
        this.props.store.drawAll();
    }

    toggleCheckAllLogpoints = () => {
        if (this.props.store.hiddenLogpointIds.length === 0) {
            this.props.store.hiddenLogpointIds = Array.from(new Set(this.props.store.latestServerData.logpoints.map(x => x.modelId)));
        } else {
            this.props.store.hiddenLogpointIds = [];
        }
        this.props.store.drawAll();
    }

    toggleCheckAllPipes = () => {
        if (this.props.store.hiddenPipeIds.length === 0) {
            this.props.store.hiddenPipeIds = Array.from(new Set(this.props.store.latestServerData.pipes.map(x => x.modelId)));
        } else {
            this.props.store.hiddenPipeIds = [];
        }
        this.props.store.drawAll();
    }

    render() {
        const { store, hasPipes, hasLayers } = this.props;

        return (
            <div className='cross-section__models_wrapper'>
                <div>
                    {store.loadSelection?.includeModelIds?.length > 0 ?
                        <div className='flex-column'>
                            <span>{__('cross_section.showing_only_included')}</span>
                            <a href="#" onClick={this.loadAllProjectModels}>{__('cross_section.use_all_project_models')}</a>
                        </div> : null
                    }
                </div>

                <table>
                    <thead>
                        <tr>
                            { hasPipes && <th><Button secondary tooltip={'Pipes: select all/none'} icon="iki iki-circle" onClick={this.toggleCheckAllPipes} disabled={!store.latestServerData?.pipes?.length}/></th> }
                            <th><Button secondary tooltip={'Logpoints: select all/none'} icon='iki iki-asbuilt' onClick={this.toggleCheckAllLogpoints} disabled={!store.latestServerData?.logpoints?.length}/></th>
                            <th><Button secondary tooltip={'Models: select all/none'} icon='iki iki-cross-section' onClick={this.toggleCheckAllModels} disabled={!store.latestServerData?.models?.length}/></th>
                            { hasLayers && <th><Button secondary tooltip={'Show/hide layers'} icon={ this.showLayersInUI ? 'iki iki-list-collapse' : 'iki iki-list-expand'} onClick={this.toggleShowLayers}/></th> }
                        </tr>
                    </thead>
                    <tbody>
                        {store.getModelsControls.map(m => {
                            return <React.Fragment key={m.modelId}>
                                <tr>
                                    { hasPipes && <td className='text-center'>
                                            {m.isPipe &&
                                                <input type="checkbox" checked={!store.hiddenPipeIds.includes(m.modelId)} onClick={() => this.toggleCheckPipe(m.modelId)}/>
                                            }
                                        </td>
                                    }
                                    <td className='text-center'>
                                        {m.isLogpoint &&
                                            <input type="checkbox" checked={!store.hiddenLogpointIds.includes(m.modelId)} onClick={() => this.toggleCheckLogpoints(m.modelId)}/>
                                        }
                                    </td>
                                    <td className="text-center">
                                        {m.isModel &&
                                            <input type="checkbox" checked={!store.hiddenModelIds.includes(m.modelId)} onClick={() => this.toggleCheckModel(m.modelId, m.children)}/>
                                        }
                                    </td>
                                    <td>
                                        <span className='break-text' style={{ color: m.color ? 'rgb(' + m.color + ')' : null }}>{m.name}</span>
                                    </td>
                                </tr>
                                {this.showLayersInUI && m.children.map(child => {
                                    return <tr key={child.modelId}>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                        <td>
                                            <div className="flex-row ai-center">
                                                <input type="checkbox" checked={!store.hiddenModelIds.includes(child.modelId)} onClick={() => this.toggleCheckModel(child.modelId)}/>
                                                <span className="break-text" style={{ marginLeft: 5, color: child.color ? 'rgb(' + child.color + ')' : null }}>{child.name}</span>
                                            </div>
                                        </td>
                                    </tr>
                                })}
                            </React.Fragment>
                        })}
                    </tbody>
                </table>
            </div>
        );
    }
}
